import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import { FencyProvider } from "@fencyai/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router-dom";
import { useListContractsQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { ExploreMemoriesChat } from "../../../common/fency/ExploreMemoriesChat";
import { fencyPromise } from "../../../common/fency/fencySetup";
import { useFency } from "../../../common/fency/useFency";
import { useSub } from "../../../common/subscription/useSub";
import { urls } from "../../../urls";

const ContractSearchChatV2 = () => {
  const { t } = useTranslation();
  // A question typed into the home search bar arrives here as ?q= and is asked on arrival, so the
  // reader does not have to type it a second time.
  const [searchParams] = useSearchParams();
  const initialPrompt = searchParams.get("q") ?? undefined;
  const authState = useLoggedInWithOrgContextState();
  const { fetchCreateStreamClientToken, fetchCreateExploreMemoriesClientToken, fetchFencyFileDownloadClientToken } =
    useFency();

  // One real contract from the org (cheap: limit 1), so the first example names an actual supplier
  // rather than being generic.
  const { data: contractList } = useListContractsQuery({ orgId: authState.selectedOrg.id, limit: 1 });

  const examplePrompts = useMemo(() => {
    const counterparty = contractList?.contracts[0]?.counterparty;
    const supplier = counterparty?.organization?.name ?? counterparty?.organizationEntry?.name;
    return [
      supplier ? t("Which contracts do we have with {{name}}?", { name: supplier }) : null,
      t("Which contracts renew in the next 90 days?"),
      t("Which contracts have the highest total price?"),
    ].filter((prompt): prompt is string => prompt != null);
  }, [contractList, t]);

  return (
    <Flex direction="column" h="full" w="full" maxW="3xl" mx="auto" gap="4">
      <Box>
        <Heading as="h1" size="lg" color="smText">
          {t("Search in contracts")}
        </Heading>
        <Text color="smMuted" fontSize="sm" mt="1">
          {t("Find contracts and their documents in natural language.")}
        </Text>
      </Box>
      <FencyProvider fency={fencyPromise} fetchCreateStreamClientToken={fetchCreateStreamClientToken}>
        <Flex flex="1" minH={0} overflow="hidden">
          <ExploreMemoriesChat
            fetchCreateExploreMemoriesClientToken={fetchCreateExploreMemoriesClientToken}
            fetchFencyFileDownloadClientToken={fetchFencyFileDownloadClientToken}
            examplePrompts={examplePrompts}
            initialPrompt={initialPrompt}
          />
        </Flex>
      </FencyProvider>
    </Flex>
  );
};

export const ContractSearchPageV2 = () => {
  const { canUseContractSearch } = useSub();

  // Feature- and role-gated; hide the page entirely for orgs without access or users without a
  // contract view role (the nav entry is gated too).
  if (!canUseContractSearch) {
    return <Navigate to={urls.v2.home} replace />;
  }

  return <ContractSearchChatV2 />;
};
